export interface GroupJoinResultGuardInput {
    requestedVesselImoNumber: number;
    selectedVesselImoNumber: number | undefined;
    hasLoggedInUser: boolean;
    isCurrentConnection: boolean;
}

export interface ConnectionGeneration {
    connection: number;
    vessel: number;
    session: number;
}

export interface GroupJoinRequestGuardInput extends GroupJoinResultGuardInput {
    requestedGeneration: ConnectionGeneration;
    currentGeneration: ConnectionGeneration;
}

/**
 * Rejects late JoinGroup responses so a prior vessel or logged-out session
 * cannot update the state displayed for the currently selected vessel.
 */
export const canApplyGroupJoinResult = (input: GroupJoinResultGuardInput): boolean =>
    input.requestedVesselImoNumber === input.selectedVesselImoNumber && input.hasLoggedInUser && input.isCurrentConnection;

/**
 * Checks a retry before it can call JoinGroup. Comparing generation values in
 * addition to vessel/authentication state prevents an old timer from issuing
 * a request after a connection, vessel, or user session was replaced.
 */
export const canIssueGroupJoinRequest = (input: GroupJoinRequestGuardInput): boolean =>
    canApplyGroupJoinResult(input) &&
    input.requestedGeneration.connection === input.currentGeneration.connection &&
    input.requestedGeneration.vessel === input.currentGeneration.vessel &&
    input.requestedGeneration.session === input.currentGeneration.session;

/**
 * useApi deliberately returns undefined for a failed GET. JoinGroup's
 * successful endpoint response is a body, so undefined can only mean that
 * the request did not receive the required successful API result.
 */
export const isConfirmedGroupJoinResponse = (response: unknown): boolean => response !== undefined;

export class GroupJoinRetryScheduler {
    private readonly timers = new Set<ReturnType<typeof setTimeout>>();

    public schedule(callback: () => void, delayMs: number): void {
        const timer = setTimeout(() => {
            this.timers.delete(timer);
            callback();
        }, delayMs);
        this.timers.add(timer);
    }

    public cancelAll(): void {
        this.timers.forEach((timer) => clearTimeout(timer));
        this.timers.clear();
    }
}
